import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { IMG, TEAM, RESULT_STAGES, RESULT_VIEWS, BEFORE_AFTER } from './dionData';
import { Reveal, ThinLine } from './DionShared';

/* SCENE 08 — DONOR AREA */
export const DonorScene = () => {
  const reduced = useReducedMotion();
  const points = [[54, 40], [62, 46], [46, 48], [58, 54], [50, 58], [66, 56], [42, 42], [60, 62]];
  return (
    <section className="relative overflow-hidden bg-[#0a0d14] py-24 lg:py-36" data-testid="dion-donor">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal className="relative order-2 aspect-[4/3] overflow-hidden lg:order-1">
          <motion.img
            src={IMG.donor} alt="Symbolische Darstellung des Spenderbereichs am Hinterkopf" className="dh-media-img"
            initial={reduced ? false : { scale: 1.08 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          />
          {points.map(([x, y], i) => (
            <motion.span key={i} className="absolute h-1.5 w-1.5 rounded-full bg-[#5b8da8]" style={{ left: `${x}%`, top: `${y}%` }}
              initial={reduced ? false : { opacity: 0, scale: 0.4 }} whileInView={{ opacity: 0.65, scale: 1 }} viewport={{ once: true }}
              transition={{ delay: 0.4 + i * 0.12, duration: 0.5 }} />
          ))}
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <span className="dh-eyebrow">08 — Spenderbereich</span>
            <h2 className="dh-display mt-6 text-[clamp(2rem,5vw,3.6rem)] text-white">
              NICHT NUR DIE NEUE<br /><span className="text-white/55">HAARLINIE ZÄHLT.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-7">
            <ThinLine width="60px" />
            <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
              Auch der Umgang mit dem Spenderbereich ist Teil einer sorgfältigen Planung. Eine gleichmäßige,
              schonende Entnahme trägt zu einem ruhigen Gesamtbild bei.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* SCENE 09 — MEDICINE × ENGINEERING */
export const MedicineEngineeringScene = () => {
  const reduced = useReducedMotion();
  const shots = [IMG.opRaum, IMG.instrument, IMG.follicle, IMG.flur];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setI((p) => (p + 1) % shots.length), 3600);
    return () => clearInterval(t);
  }, [reduced, shots.length]);

  return (
    <section id="expertise" className="relative bg-[#07090e] py-24 lg:py-36" data-testid="dion-medicine-engineering">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="relative order-2 aspect-[4/3] overflow-hidden bg-[#111827] lg:order-1">
            <AnimatePresence mode="sync">
              <motion.img key={i} src={shots[i]} alt="Medizin und Ingenieurtechnik — Symbolbild" className="dh-media-img absolute inset-0"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2, ease: 'easeInOut' }} />
            </AnimatePresence>
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <span className="dh-eyebrow">09 — Expertise</span>
              <h2 className="dh-display mt-6 text-[clamp(2rem,5vw,3.6rem)] text-white">
                MEDIZIN TRIFFT<br /><span className="text-white/55">INGENIEURTECHNIK.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="mt-7">
              <ThinLine width="60px" />
              <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">Präzise Technik. Persönliche Verantwortung.</p>
            </Reveal>
          </div>
        </div>

        <div id="ueber-uns" data-testid="dion-ueber-uns" className="mt-20 border-t border-white/10 pt-12">
          <Reveal><span className="dh-kicker text-white/50">Dion Hair Clinic · Team</span></Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {TEAM.map((m, idx) => (
              <Reveal key={m.name} delay={idx * 0.08}>
                <div className="aspect-[4/5] overflow-hidden bg-[#111827]">
                  <img src={m.image} alt={m.name} className="dh-media-img grayscale" loading="lazy" />
                </div>
                <h3 className="mt-4 text-base font-medium text-white">{m.name}</h3>
                <p className="dh-kicker mt-1 text-white/40">Funktion — Platzhalter</p>
              </Reveal>
            ))}
          </div>
          <p className="dh-kicker mt-8 leading-relaxed text-white/35">
            Team- und Qualifikationsangaben sind Platzhalter und werden mit den finalen Daten der Klinik ergänzt.
          </p>
        </div>
      </div>
    </section>
  );
};

/* SCENE 10 — RESULTS TIMELINE */
export const ResultsScene = () => {
  const [view, setView] = useState('front');
  const stageImage = (id) => (id === 'vor' ? BEFORE_AFTER.before : id === 'm12' ? BEFORE_AFTER.after : null);
  return (
    <section id="ergebnisse" className="relative bg-[#0a0d14] py-24 lg:py-36" data-testid="dion-results">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <span className="dh-eyebrow">10 — Ergebnisse</span>
            <h2 className="dh-display mt-5 text-[clamp(2rem,5vw,3.6rem)] text-white">Verlauf statt Versprechen.</h2>
          </Reveal>
          <Reveal delay={0.1} className="flex gap-2">
            {RESULT_VIEWS.map((v) => (
              <button key={v.id} onClick={() => setView(v.id)} data-testid={`results-timeline-filter-${v.id}`}
                className={`dh-kicker border px-4 py-2.5 transition-colors ${view === v.id ? 'border-[#5b8da8] text-white' : 'border-white/12 text-white/45 hover:text-white/75'}`}>
                {v.label}
              </button>
            ))}
          </Reveal>
        </div>

        <div className="mt-12 flex gap-5 overflow-x-auto pb-4 lg:grid lg:grid-cols-6 lg:gap-4 lg:overflow-visible" data-testid="results-timeline">
          {RESULT_STAGES.map((s, i) => {
            const img = stageImage(s.id);
            return (
              <div key={s.id} className="w-[68vw] shrink-0 sm:w-[42vw] lg:w-auto">
                <div className="dh-kicker mb-3 flex items-center gap-2 text-white/50"><span className="text-[#5b8da8]">0{i + 1}</span>{s.label}</div>
                <div className="relative aspect-[3/4] overflow-hidden bg-[#111827]">
                  {img ? (
                    <img src={img} alt={`Fallbeispiel — ${s.label}`} className="dh-media-img grayscale" loading="lazy" />
                  ) : (
                    <div className="flex h-full items-center justify-center"><span className="dh-kicker text-white/25">Bild folgt</span></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <ThinLine width="100%" className="mt-2 opacity-30" />
        <p className="dh-kicker mt-6 leading-relaxed text-white/35">
          Fallbeispiel mit Platzhaltern. Ergebnisse und Heilungsverlauf sind individuell und können variieren.
          Ansicht „{RESULT_VIEWS.find((v) => v.id === view)?.label}“ — weitere Aufnahmen folgen.
        </p>
      </div>
    </section>
  );
};
